import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Icon } from './Icon.js';
import { supabase } from '../supabase';
import { useUserProtocolStatus } from '../contexts/UserProtocolStatusContext';
import { 
  FiRefreshCw, 
  FiCoffee, 
  FiCircle, 
  FiStar, 
  FiFeather, 
  FiBox, 
  FiCpu, 
  FiDroplet, 
  FiWind, 
  FiAperture, 
  FiHeart, 
  FiMoon, 
  FiThermometer, 
  FiSun, 
  FiTarget, 
  FiActivity, 
  FiZap 
} from 'react-icons/fi';
import './ProtocolShield.css';

// Static objects - moved outside component to prevent recreation on every render
const categoryIcons = {
  'Lifestyle & Recovery': FiRefreshCw,
  'Diet & Nutrition': FiCoffee,
  'Supplement': FiCircle,
  'Vitamin & Mineral': FiStar,
  'Herbal & Natural Extracts': FiFeather,
  'Pharmaceutical': FiBox,
  'Medical Technology & Devices': FiCpu,
  'Topical & Enzyme Treatments': FiDroplet,
  'Detox & Absorption': FiWind,
  'Cold Therapy': FiAperture,
  'Nutrition': FiCoffee,
  'Mindfulness': FiHeart,
  'Sleep': FiMoon,
  'Heat Therapy': FiThermometer,
  'Breathing': FiWind,
  'Nature Therapy': FiSun,
  'Light Therapy': FiTarget,
  'Exercise': FiActivity,
  'Supplements': FiCircle,
  'Technology': FiCpu,
  'Recovery': FiRefreshCw,
  'Lifestyle': FiStar,
};

// Static rotations array for biohack positioning
const ROTATIONS = ['-2deg', '1deg', '-1deg', '2deg', '-1.5deg', '1.5deg', '-2deg', '0.5deg'];

/**
 * ProtocolShield Component
 * 
 * A modern protocol card component with glassmorphism design.
 * Features biohack images peeking from behind a frosted glass overlay.
 * 
 * @param {Object} protocol - Protocol data object
 * @param {string} size - Size variant: 'small' | 'medium' | 'large' (default: 'medium')
 * @param {string} variant - Style variant: 'default' | 'compact' | 'expanded' (default: 'default')
 * @param {string} className - Additional CSS classes
 * @param {Function} onClick - Click handler for shield
 * @param {Function} onBiohackClick - Click handler for individual biohacks
 */
const ProtocolShield = ({
  protocol,
  size = 'medium',
  variant = 'default',
  className = '',
  onClick,
  onBiohackClick,
  onScheduleClick,
  scheduleData = {},
  onStageProtocol,
  onActivateProtocol,
  onDeactivateProtocol,
  onDeleteProtocol,
  onLike,
  onDislike,
  userVote = null, // Current user's vote: 1 for like, -1 for dislike, null for none
  disableFlip = false, // Disable flip functionality when true
  hideFabMenu = false, // Hide FAB menu when true
  hideInspectButton = false, // Hide inspect button when true
  participants = [], // Array of participant objects with { id, username, profile_picture_url }
  onAddToStaged = null, // Handler to add protocol to staged protocols (for protocol attachments on posts)
  onNotesClick = null // Handler to open notes sidebar
}) => {
  const { hasProtocol } = useUserProtocolStatus();
  const [isFabMenuOpen, setIsFabMenuOpen] = useState(false);
  const [showPopTooltip, setShowPopTooltip] = useState(false);
  const [showAdhTooltip, setShowAdhTooltip] = useState(false);
  const [showBigTooltip, setShowBigTooltip] = useState(false);
  const [showNameTooltip, setShowNameTooltip] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const isInStaged = !!(onAddToStaged && protocol?.id && hasProtocol(protocol.id));
  const [showBiohackViewer, setShowBiohackViewer] = useState(false);
  const fabMenuRef = useRef(null);
  const fabButtonRef = useRef(null);
  const inspectButtonRef = useRef(null);
  
  // Detect touch/coarse pointer (mobile) so we can avoid flipping when user taps a tooltip area
  const isTouchDevice = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(pointer: coarse)').matches || ('ontouchstart' in window);
  }, []);
  
  // Memoize container classes
  const containerClasses = useMemo(() => [
    'protocol-shield-container',
    `protocol-shield-container--${size}`,
    `protocol-shield-container--${variant}`,
    className
  ].filter(Boolean).join(' '), [size, variant, className]);

  // Memoize biohacks extraction - expensive operation with sorting/mapping
  const biohacks = useMemo(() => {
    if (protocol?.biohacks) {
      return protocol.biohacks;
    } else if (protocol?.protocol_hacks) {
      return protocol.protocol_hacks
        .sort((a, b) => (a.position || 0) - (b.position || 0))
        .map(ph => ph.biohacks)
        .filter(Boolean);
    } else if (protocol?.hacks) {
      return protocol.hacks;
    }
    return [];
  }, [protocol?.biohacks, protocol?.protocol_hacks, protocol?.hacks]);
  
  // Memoize protocol_hacks structure
  const protocolHacks = useMemo(() => protocol?.protocol_hacks || [], [protocol?.protocol_hacks]);
  
  // Memoize POP score calculation - optimize to single pass through array
  const popScore = useMemo(() => {
    // Prefer calculating from protocol_ratings if available (more accurate/real-time)
    if (protocol?.protocol_ratings && protocol.protocol_ratings.length > 0) {
      let likes = 0;
      let dislikes = 0;
      for (const rating of protocol.protocol_ratings) {
        if (rating.rating === 1) likes++;
        else if (rating.rating === -1) dislikes++;
      }
      const calculated = likes - dislikes;
      return calculated;
    }
    
    // Fallback to pop_score if protocol_ratings not available
    if (protocol?.pop_score != null) {
      return protocol.pop_score;
    }
    
    return 0;
  }, [protocol?.pop_score, protocol?.protocol_ratings]);
  
  // Get ADH score (adherence)
  const adhScore = useMemo(() => 
    protocol?.adherence_score != null ? Math.round(protocol.adherence_score) : 0,
    [protocol?.adherence_score]
  );

  // Calculate protocol score for the orb (average of normalized POP and ADH)
  const protocolScore = useMemo(() => {
    // When both POP and ADH are 0, show 0 so the orb doesn't display 25 (which looks odd)
    const adh = protocol?.adherence_score != null ? protocol.adherence_score : 0;
    if (popScore === 0 && adh === 0) {
      return 0;
    }
    // Normalize POP to 0-100 scale (POP can be negative, so we normalize assuming range of -100 to +100)
    // This maps: -100 -> 0, 0 -> 50, +100 -> 100
    const normalizedPOP = Math.max(0, Math.min(100, ((popScore + 100) / 200) * 100));
    // Average of normalized POP and ADH
    const average = (normalizedPOP + adh) / 2;
    return average;
  }, [popScore, protocol?.adherence_score]);

  // Format protocol score for display: decimals for 0-9, no decimals for 10+
  const formattedProtocolScore = useMemo(() => {
    if (protocolScore < 10) {
      return protocolScore.toFixed(1);
    } else {
      return Math.round(protocolScore).toString();
    }
  }, [protocolScore]);

  // Memoize event handlers to prevent recreation on every render
  const handleBiohackClick = useCallback((e, biohack) => {
    e.stopPropagation(); // Prevent triggering the shield onClick
    if (onBiohackClick) {
      onBiohackClick(biohack);
    }
  }, [onBiohackClick]);

  // Handle shield click to flip
  const handleShieldClick = useCallback((e) => {
    // Don't flip if clicking on FAB menu, button, or inspect button
    if (
      fabMenuRef.current?.contains(e.target) ||
      fabButtonRef.current?.contains(e.target) ||
      inspectButtonRef.current?.contains(e.target)
    ) {
      return;
    }
    
    // Close FAB menu if open (using functional update to avoid dependency)
    setIsFabMenuOpen(prev => prev ? false : prev);
    
    // On mobile/touch: don't flip when user tapped a tooltip area so they can read the tooltip
    const tooltipAreaSelector = '.protocol-shield-name-wrapper, .protocol-score-badge, .protocol-score-orb';
    const isTooltipArea = e.target.closest?.(tooltipAreaSelector);
    if (isTouchDevice && isTooltipArea) {
      return;
    }
    
    // On touch, when flipping, close any open tooltips so state is clean
    if (isTouchDevice) {
      setShowPopTooltip(false);
      setShowAdhTooltip(false);
      setShowBigTooltip(false);
      setShowNameTooltip(false);
    }
    
    // Only flip if not disabled
    if (!disableFlip) {
      setIsFlipped(prev => !prev);
    }
    
    // Also call the original onClick handler if provided
    if (onClick) {
      onClick(e);
    }
  }, [disableFlip, onClick, isTouchDevice]);

  // Handle inspect button click to show biohack viewer
  const handleInspectClick = useCallback((e) => {
    e.stopPropagation();
    if (biohacks.length > 0) {
      setShowBiohackViewer(true);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
  }, [biohacks.length]);

  // Handle closing biohack viewer
  const handleCloseBiohackViewer = useCallback(() => {
    setShowBiohackViewer(false);
    document.body.style.overflow = '';
  }, []);

  // Close FAB menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isFabMenuOpen &&
        fabMenuRef.current &&
        !fabMenuRef.current.contains(e.target) &&
        fabButtonRef.current &&
        !fabButtonRef.current.contains(e.target)
      ) {
        setIsFabMenuOpen(false);
      }
    };

    if (isFabMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFabMenuOpen]);

  // Close FAB menu on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isFabMenuOpen) {
        setIsFabMenuOpen(false);
      }
      if (e.key === 'Escape' && showBiohackViewer) {
        handleCloseBiohackViewer();
      }
    };

    if (isFabMenuOpen || showBiohackViewer) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isFabMenuOpen, showBiohackViewer, handleCloseBiohackViewer]);

  // Cleanup body overflow when component unmounts or modal closes
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Toggle FAB menu
  const handleFabButtonClick = useCallback((e) => {
    e.stopPropagation();
    setIsFabMenuOpen(prev => !prev);
  }, []);

  // Handle menu item clicks - memoized with protocol.id as dependency
  const handleScheduleClick = useCallback((e) => {
    e.stopPropagation();
    if (onScheduleClick) {
      onScheduleClick(protocol);
    }
  }, [onScheduleClick, protocol]);

  const handleStageProtocol = useCallback((e) => {
    e.stopPropagation();
    if (onStageProtocol) {
      onStageProtocol(protocol);
    }
  }, [onStageProtocol, protocol]);

  const handleLike = useCallback((e) => {
    e.stopPropagation();
    if (onLike) {
      onLike(protocol);
    }
  }, [onLike, protocol]);

  const handleDislike = useCallback((e) => {
    e.stopPropagation();
    if (onDislike) {
      onDislike(protocol);
    }
  }, [onDislike, protocol]);

  const handleActivateProtocol = useCallback((e) => {
    e.stopPropagation();
    setIsFabMenuOpen(false);
    if (onActivateProtocol) {
      onActivateProtocol(protocol);
    }
  }, [onActivateProtocol, protocol]);

  const handleDeactivateProtocol = useCallback((e) => {
    e.stopPropagation();
    setIsFabMenuOpen(false);
    if (onDeactivateProtocol) {
      onDeactivateProtocol(protocol);
    }
  }, [onDeactivateProtocol, protocol]);

  const handleDeleteProtocol = useCallback((e) => {
    e.stopPropagation();
    setIsFabMenuOpen(false);
    if (onDeleteProtocol) {
      onDeleteProtocol(protocol);
    }
  }, [onDeleteProtocol, protocol]);

  const handleAddToStaged = useCallback(async (e) => {
    e.stopPropagation();
    setIsFabMenuOpen(false);
    if (onAddToStaged) {
      await onAddToStaged(protocol);
      // Context refresh in parent will update isInStaged via hasProtocol
    }
  }, [onAddToStaged, protocol]);

  const handleNotesClick = useCallback((e) => {
    e.stopPropagation();
    setIsFabMenuOpen(false);
    if (onNotesClick) {
      onNotesClick(protocol);
    }
  }, [onNotesClick, protocol]);

  // Memoize protocol status calculations
  const isActive = protocol?.status === 'active';
  const isStaged = protocol?.status === 'staged';
  const showSchedule = isActive && onScheduleClick;
  const showStage = !isActive && !isStaged && onStageProtocol;
  const showActivate = isStaged && onActivateProtocol;
  const showDeactivate = isActive && onDeactivateProtocol;
  // Show "Add to Staged" when onAddToStaged is provided (for protocol attachments on posts)
  // The handler itself will check if the protocol already exists
  const showAddToStaged = !!onAddToStaged;

  // Memoize date formatting
  const formattedDate = useMemo(() => {
    if (!protocol?.created_at) return 'Date unknown';
    try {
      const date = new Date(protocol.created_at);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return 'Date unknown';
    }
  }, [protocol?.created_at]);

  // Memoize creator username
  const creatorUsername = useMemo(() => {
    if (protocol?.created_by_username) {
      return protocol.created_by_username;
    }
    if (protocol?.user_profiles?.username) {
      return protocol.user_profiles.username;
    }
    return 'Unknown';
  }, [protocol?.created_by_username, protocol?.user_profiles?.username]);

  // Memoize unique categories - expensive operation with multiple iterations
  const { uniqueCategories, displayedCategories } = useMemo(() => {
    const categories = new Set();
    
    // Check protocol_hacks structure for categories
    protocolHacks.forEach((ph) => {
      const category = ph?.biohacks?.category;
      if (category && category.trim()) {
        categories.add(category);
      }
    });
    
    // Also check direct biohacks array
    biohacks.forEach((biohack) => {
      const category = biohack?.category || biohack?.biohacks?.category;
      if (category && category.trim()) {
        categories.add(category);
      }
    });
    
    const unique = Array.from(categories);
    return {
      uniqueCategories: unique,
      displayedCategories: unique
    };
  }, [protocolHacks, biohacks]);

  return (
    <div 
      className={`${containerClasses} ${isFlipped ? 'protocol-shield-flipped' : ''}`}
      onClick={handleShieldClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="protocol-shield-wrapper">
        <div className="protocol-shield-inner">
          {/* Front side */}
          <div className="protocol-shield-face protocol-shield-front">
            <div className="protocol-shield-background"></div>

            {/* FAB Menu Button */}
            {!hideFabMenu && (
              <button
                ref={fabButtonRef}
                className="protocol-shield-fab-btn"
                onClick={handleFabButtonClick}
                title="More options"
                aria-label="Open protocol menu"
                aria-expanded={isFabMenuOpen}
              >
                <Icon name="MoreVertical" className="protocol-shield-fab-icon" />
              </button>
            )}

            {/* Inspect Protocol Button */}
            {biohacks.length > 0 && !hideInspectButton && (
              <button
                ref={inspectButtonRef}
                className="protocol-shield-inspect-btn"
                onClick={handleInspectClick}
                title="Inspect protocol"
                aria-label="Inspect protocol biohacks"
              >
                <Icon name="Eye" className="protocol-shield-inspect-icon" />
              </button>
            )}

            {/* FAB Menu */}
            {!hideFabMenu && isFabMenuOpen && (
              <div 
                ref={fabMenuRef}
                className="protocol-shield-fab-menu"
                role="menu"
                aria-orientation="vertical"
              >
                {showSchedule && (
                  <button
                    className="protocol-shield-fab-menu-item"
                    onClick={handleScheduleClick}
                    role="menuitem"
                  >
                    <Icon name="Clock" className="protocol-shield-fab-menu-icon" />
                    <span>Schedule</span>
                  </button>
                )}
                {showStage && (
                  <button
                    className="protocol-shield-fab-menu-item"
                    onClick={handleStageProtocol}
                    role="menuitem"
                  >
                    <Icon name="Bookmark" className="protocol-shield-fab-menu-icon" />
                    <span>Stage Protocol</span>
                  </button>
                )}
                {showActivate && (
                  <button
                    className="protocol-shield-fab-menu-item"
                    onClick={handleActivateProtocol}
                    role="menuitem"
                  >
                    <Icon name="Play" className="protocol-shield-fab-menu-icon" />
                    <span>Activate</span>
                  </button>
                )}
                {showDeactivate && (
                  <button
                    className="protocol-shield-fab-menu-item"
                    onClick={handleDeactivateProtocol}
                    role="menuitem"
                  >
                    <Icon name="Pause" className="protocol-shield-fab-menu-icon" />
                    <span>Deactivate</span>
                  </button>
                )}
                {showAddToStaged && (
                  <button
                    className={`protocol-shield-fab-menu-item ${isInStaged ? 'active' : ''}`}
                    onClick={handleAddToStaged}
                    role="menuitem"
                  >
                    <Icon name="Plus" className="protocol-shield-fab-menu-icon" />
                    <span>Add to Staged</span>
                  </button>
                )}
                {onNotesClick && (
                  <button
                    className="protocol-shield-fab-menu-item"
                    onClick={handleNotesClick}
                    role="menuitem"
                  >
                    <Icon name="FileText" className="protocol-shield-fab-menu-icon" />
                    <span>Notes</span>
                  </button>
                )}
                <button
                  className={`protocol-shield-fab-menu-item ${userVote === 1 ? 'active' : ''}`}
                  onClick={handleLike}
                  role="menuitem"
                >
                  <Icon name="ThumbsUp" className="protocol-shield-fab-menu-icon" />
                  <span>Like</span>
                </button>
                <button
                  className={`protocol-shield-fab-menu-item ${userVote === -1 ? 'active' : ''}`}
                  onClick={handleDislike}
                  role="menuitem"
                >
                  <Icon name="ThumbsDown" className="protocol-shield-fab-menu-icon" />
                  <span>Dislike</span>
                </button>
                {onDeleteProtocol && (
                  <button
                    className="protocol-shield-fab-menu-item protocol-shield-fab-menu-item--delete"
                    onClick={handleDeleteProtocol}
                    role="menuitem"
                  >
                    <Icon name="Trash2" className="protocol-shield-fab-menu-icon" />
                    <span>Delete</span>
                  </button>
                )}
              </div>
            )}

            <div className="protocol-shield-content">
              {/* Biohack Images - Back Layer - Evenly distributed */}
              {biohacks.length > 0 && (() => {
                // Calculate positioning constants once outside the map
                const cardWidth = 320;
                const iconWidth = 110;
                const minPadding = 10;
                const totalIcons = biohacks.length;
                const firstIconLeft = minPadding;
                const lastIconLeft = cardWidth - minPadding - iconWidth;
                const totalDistance = lastIconLeft - firstIconLeft;
                const spacingBetween = totalIcons > 1 ? totalDistance / (totalIcons - 1) : 0;
                
                return (
                  <div className="protocol-biohacks-back-layer">
                    {biohacks.map((biohack, index) => {
                      // Calculate position for this icon
                      const constrainedLeft = firstIconLeft + (index * spacingBetween);
                      
                      // Subtle rotations for slight jumble (less jumbled) - cycle through rotations
                      const rotation = ROTATIONS[index % ROTATIONS.length] || '0deg';
                    
                    return (
                        <div 
                          key={biohack.id || index}
                          className="protocol-biohack-image-back"
                          style={{ 
                            zIndex: totalIcons - index,
                            left: `${constrainedLeft}px`,
                            top: '20px',
                            transform: `rotate(${rotation})`
                          }}
                        onClick={(e) => handleBiohackClick(e, biohack)}
                        role="button"
                        tabIndex={0}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleBiohackClick(e, biohack);
                          }
                        }}
                      >
                        <img 
                          src={biohack.image_url || biohack.imageUrl || '/default-biohack.jpg'} 
                          alt={biohack.name || 'Biohack'}
                          className="protocol-biohack-image-back-img"
                          loading="lazy"
                          onError={(e) => {
                            e.target.src = '/default-biohack.jpg';
                          }}
                        />
                      </div>
                    );
                  })}
                  </div>
                );
              })()}

              {/* Bottom Section - Glassmorphism overlay with Protocol Name, Scores and Orb */}
              <div className="protocol-front-section">

                {/* Protocol Name */}
                <div 
                  className="protocol-shield-name-wrapper"
                  onMouseEnter={() => setShowNameTooltip(true)}
                  onMouseLeave={() => setShowNameTooltip(false)}
                  onTouchStart={() => isTouchDevice && setShowNameTooltip(true)}
                >
                  <h3 className="protocol-shield-name">
                    {protocol?.name || 'Protocol'}
                  </h3>
                  {showNameTooltip && (
                    <div className="protocol-shield-name-tooltip">
                      {protocol?.name || 'Protocol'}
                    </div>
                  )}
                </div>

                {/* Creator Username */}
                <div className="protocol-shield-creator">
                  by {creatorUsername}
                </div>

                {/* Bottom Section with Scores and Orb */}
                <div className="protocol-bottom-section">
                  {/* Scores on Left - POP, ADH */}
                  <div className="protocol-scores-left">
                    <div 
                      className="protocol-score-badge"
                      onMouseEnter={() => setShowPopTooltip(true)}
                      onMouseLeave={() => setShowPopTooltip(false)}
                      onTouchStart={() => isTouchDevice && setShowPopTooltip(true)}
                    >
                      <span className="protocol-score-label">POP</span>
                      <span className="protocol-score-value">{popScore}</span>
                      {showPopTooltip && (
                        <div className="protocol-score-tooltip">
                          <div className="tooltip-title">Popularity Score</div>
                          <div className="tooltip-content">
                            A community-driven score calculated as likes minus dislikes. 
                            Higher scores indicate protocols that have a higher number of likes compared to dislikes among users.
                          </div>
                        </div>
                      )}
                    </div>
                    <div 
                      className="protocol-score-badge"
                      onMouseEnter={() => setShowAdhTooltip(true)}
                      onMouseLeave={() => setShowAdhTooltip(false)}
                      onTouchStart={() => isTouchDevice && setShowAdhTooltip(true)}
                    >
                      <span className="protocol-score-label">ADH</span>
                      <span className="protocol-score-value">{adhScore}</span>
                      {showAdhTooltip && (
                        <div className="protocol-score-tooltip">
                          <div className="tooltip-title">Adherence Score</div>
                          <div className="tooltip-content">
                            Measures how consistently users follow this protocol. 
                            Higher scores indicate better user adherence and completion rates.
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Score Orb on Right */}
                  <div 
                    className="protocol-score-orb"
                    onMouseEnter={() => setShowBigTooltip(true)}
                    onMouseLeave={() => setShowBigTooltip(false)}
                    onTouchStart={() => isTouchDevice && setShowBigTooltip(true)}
                  >
                    <div className="protocol-score-orb-value">
                      {formattedProtocolScore}
                    </div>
                    {showBigTooltip && (
                      <div className="protocol-score-tooltip">
                        <div className="tooltip-title">Overall Score</div>
                        <div className="tooltip-content">
                          A combined score averaging the normalized Popularity score and Adherence score. 
                          This provides an overall assessment of the protocol's adherence and user engagement.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Back side */}
          <div className="protocol-shield-face protocol-shield-back">
            <div className="protocol-shield-background"></div>
            
            <div className="protocol-shield-content">
              {/* Protocol Description */}
              {protocol?.description && (
                <div className="protocol-shield-back-description">
                  {protocol.description}
                </div>
              )}
              
              {/* Created by */}
              <div className="protocol-shield-back-created-by">
                Created by {creatorUsername}
              </div>
              
              {/* Created date */}
              <div className="protocol-shield-back-created-date">
                {formattedDate}
              </div>
              
              {/* Category Icons - Bottom Center */}
              {displayedCategories.length > 0 ? (
                <div className="protocol-category-icons protocol-category-icons-back">
                  {displayedCategories.map((category, index) => {
                    const IconComponent = categoryIcons[category] || FiZap;
                    return (
                      <div key={`${category}-${index}`} className="protocol-category-icon">
                        <IconComponent />
                        <div className="protocol-category-icon-tooltip">{category}</div>
                      </div>
                    );
                  })}
                </div>
              ) : biohacks.length > 0 ? (
                // Fallback: show a default icon if we have biohacks but no categories
                <div className="protocol-category-icons protocol-category-icons-back">
                  <div className="protocol-category-icon">
                    <FiZap />
                    <div className="protocol-category-icon-tooltip">Biohacks</div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Biohack Viewer Modal - portaled to body so overlay covers full viewport (sidebars, post cards) */}
      {showBiohackViewer && createPortal(
        <div 
          className="protocol-biohack-viewer-overlay"
          onClick={handleCloseBiohackViewer}
          role="dialog"
          aria-modal="true"
          aria-labelledby="protocol-biohack-viewer-title"
        >
          <div 
            className="protocol-biohack-viewer-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="protocol-biohack-viewer-header">
              <h3 id="protocol-biohack-viewer-title" className="protocol-biohack-viewer-title">
                {protocol?.name || 'Protocol'} Biohacks
              </h3>
              <button
                className="protocol-biohack-viewer-close"
                onClick={handleCloseBiohackViewer}
                aria-label="Close biohack viewer"
              >
                <Icon name="X" className="protocol-biohack-viewer-close-icon" />
              </button>
            </div>
            <div className="protocol-biohack-viewer-grid">
              {biohacks.map((biohack, index) => (
                <div
                  key={biohack.id || index}
                  className="protocol-biohack-viewer-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBiohackClick(e, biohack);
                    handleCloseBiohackViewer();
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleBiohackClick(e, biohack);
                      handleCloseBiohackViewer();
                    }
                  }}
                >
                  <img 
                    src={biohack.image_url || biohack.imageUrl || '/default-biohack.jpg'} 
                    alt={biohack.name || 'Biohack'}
                    className="protocol-biohack-viewer-image"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = '/default-biohack.jpg';
                    }}
                  />
                  {biohack.name && (
                    <div className="protocol-biohack-viewer-name">
                      {biohack.name}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

ProtocolShield.propTypes = {
  protocol: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    biohacks: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      image_url: PropTypes.string,
      imageUrl: PropTypes.string
    })),
    score: PropTypes.number,
    status: PropTypes.string
  }).isRequired,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  variant: PropTypes.oneOf(['default', 'compact', 'expanded']),
  className: PropTypes.string,
  onClick: PropTypes.func,
  onBiohackClick: PropTypes.func,
  onScheduleClick: PropTypes.func,
  onStageProtocol: PropTypes.func,
  onActivateProtocol: PropTypes.func,
  onDeactivateProtocol: PropTypes.func,
  onDeleteProtocol: PropTypes.func,
  onLike: PropTypes.func,
  onDislike: PropTypes.func,
  userVote: PropTypes.oneOf([1, -1, null]),
  disableFlip: PropTypes.bool,
  hideFabMenu: PropTypes.bool,
  participants: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    username: PropTypes.string,
    profile_picture_url: PropTypes.string
  })),
  onAddToStaged: PropTypes.func,
  onNotesClick: PropTypes.func
};

// Memoize component to prevent unnecessary re-renders when props haven't changed
export default React.memo(ProtocolShield);